import { useCallback } from "react";
import {
    type InfiniteData,
    QueryClient,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ServerInferRequest } from "@ts-rest/core";
import { counterpartyContract } from "@common/contracts/counterparty.contract";
import { Counterparty } from "@common/types/counterparty";
import {
    GetCounterpartiesQuery,
    GetCounterpartiesResponse,
} from "@common/schemas/counterparty.schema";
import {
    CreateCounterpartyContactBody,
    UpdateCounterpartyContactBody,
} from "@common/schemas/counterpartyContact.schema";
import {
    CreateCounterpartyAddressBody,
    UpdateCounterpartyAddressBody,
} from "@common/schemas/counterpartyAddress.schema";
import { CounterpartyDetails } from "@common/types/counterpartyDetails";
import { CounterpartyFieldReviewDto } from "@common/schemas/counterpartyFieldReview.schema";
import {
    CounterpartyReviewField,
    GenericCounterpartyReviewField,
} from "@common/types/counterpartyFieldReview";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient as globalQueryClient } from "../queryClient";

const counterpartyContractClient = getBackendAPIClient(counterpartyContract);

const counterpartyContractBlobClient = getBackendAPIClient(
    counterpartyContract,
    "blob",
);

export const counterpartiesQueryKeys = {
    all: () => ["counterparties"] as const,
    query: (workspaceId: string) =>
        [...counterpartiesQueryKeys.all(), workspaceId] as const,
    queryWithParams: (workspaceId: string, params: GetCounterpartiesQuery) =>
        [
            ...counterpartiesQueryKeys.query(workspaceId),
            params.sort,
            params.includeRegistered,
            params.includeUsageStats,
            params.search,
            params.startDate,
            params.endDate,
            params.entityIds,
            params.basis,
            params.coaAccountIds,
            params.minAmount,
            params.maxAmount,
            params.amountDirection,
            params.amountRounded,
            params.amountRoundingLevel,
            params.isEligible1099,
            params.shouldExcludeCreditCardJournalEntries,
            params.shouldExcludePaymentProcessorJournalEntries,
            params.invoiceCounterpartiesOnly,
        ] as const,
    picker: (workspaceId: string, params: CounterpartyPickerQuery) =>
        [
            ...counterpartiesQueryKeys.query(workspaceId),
            "picker",
            params,
        ] as const,
    // Single-entity by-id lookups live under a sibling "by-id" branch that is
    // intentionally NOT a prefix-descendant of query(workspaceId). The list
    // updaters (setQueriesData/invalidateQueries on query(workspaceId)) use
    // prefix matching; keeping by-id off that prefix prevents them from
    // overwriting the single Counterparty entry with list-shaped data.
    byIdAll: (workspaceId: string) =>
        [...counterpartiesQueryKeys.all(), "by-id", workspaceId] as const,
    byId: (workspaceId: string, counterpartyId: string) =>
        [
            ...counterpartiesQueryKeys.byIdAll(workspaceId),
            counterpartyId,
        ] as const,
};

export function useCreateCounterpartyMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: Omit<
                ServerInferRequest<
                    typeof counterpartyContract.createCounterparty
                >["body"],
                "workspaceId"
            >,
        ) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const response =
                await counterpartyContractClient.createCounterparty({
                    body: {
                        ...data,
                        workspaceId,
                    },
                });

            return response.body;
        },
        onSuccess: ({ counterparty }) => {
            if (!counterparty.workspaceId) {
                return;
            }
            queryClient.invalidateQueries({
                queryKey: counterpartiesQueryKeys.query(
                    counterparty.workspaceId,
                ),
            });
            // by-id lives on a sibling key, so refresh it explicitly.
            queryClient.invalidateQueries({
                queryKey: counterpartiesQueryKeys.byIdAll(
                    counterparty.workspaceId,
                ),
            });
        },
    });
}

export function useUpdateCounterpartyMutation(
    counterparty: Counterparty,
    onSuccess?: (counterparty: Counterparty) => void,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: ServerInferRequest<
                typeof counterpartyContract.updateCounterparty
            >["body"],
        ) => {
            if (!counterparty.workspaceId) {
                throw new Error("Can't edit global counterparty");
            }

            const response =
                await counterpartyContractClient.updateCounterparty({
                    body: data,
                    params: {
                        workspaceId: counterparty.workspaceId,
                        counterpartyId: counterparty.id,
                    },
                });

            return response.body.counterparty;
        },
        onSuccess: (updatedCounterparty) => {
            handleCounterpartyUpdate(
                queryClient,
                counterparty.id,
                updatedCounterparty,
            );
            onSuccess?.(updatedCounterparty);
        },
    });
}

export async function fetchCounterpartyDetails({
    workspaceId,
    counterpartyId,
}: {
    workspaceId: string;
    counterpartyId: string;
}): Promise<CounterpartyDetails> {
    const response = await counterpartyContractClient.getCounterpartyDetails({
        params: { workspaceId, counterpartyId },
    });
    if (response.status !== 200) {
        throw new Error("Failed to load counterparty details");
    }
    return response.body;
}

// Loads a counterparty and its contact/address books from the dedicated by-id
// endpoint. Accepts a workspace-local or a global counterparty. Only fetched
// when a details panel/editor is open, so paginated directory rows stay on the
// cheaper primary-scalar list.
export function useCounterpartyDetailsQuery({
    counterpartyId,
    enabled = true,
}: {
    counterpartyId?: string;
    enabled?: boolean;
}) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: counterpartiesQueryKeys.byId(
            workspaceId ?? "",
            counterpartyId ?? "",
        ),
        enabled: enabled && Boolean(workspaceId) && Boolean(counterpartyId),
        queryFn: () => {
            if (!workspaceId || !counterpartyId) {
                throw new Error("Missing workspace or counterparty");
            }
            return fetchCounterpartyDetails({ workspaceId, counterpartyId });
        },
    });
}

// Imperative counterpart to useCounterpartyDetailsQuery for callers that need
// the freshly persisted books outside of render — e.g. after copy-global clones
// a shared counterparty, to translate a stale global child id to its local
// clone before mutating it.
export function useFetchCounterpartyDetails() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useCallback(
        async (counterpartyId: string): Promise<CounterpartyDetails> => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            return await queryClient.fetchQuery({
                queryKey: counterpartiesQueryKeys.byId(
                    workspaceId,
                    counterpartyId,
                ),
                queryFn: async (): Promise<CounterpartyDetails> => {
                    const response =
                        await counterpartyContractClient.getCounterpartyDetails(
                            {
                                params: { workspaceId, counterpartyId },
                            },
                        );
                    if (response.status !== 200) {
                        throw new Error("Failed to load counterparty details");
                    }
                    return response.body;
                },
            });
        },
        [queryClient, workspaceId],
    );
}

// Resolves a full Counterparty entity from just an id for callers that only
// hold a partial reference, such as the invoice composer opening the edit
// modal. Backed by the dedicated by-id details endpoint.
export function useCounterpartyByIdQuery({
    counterpartyId,
    enabled,
}: {
    counterpartyId?: string;
    enabled: boolean;
}) {
    const detailsQuery = useCounterpartyDetailsQuery({
        counterpartyId,
        enabled,
    });
    return {
        ...detailsQuery,
        data: detailsQuery.data?.counterparty,
    };
}

function invalidateCounterpartyDetails(
    queryClient: QueryClient,
    workspaceId: string,
    counterpartyId: string,
) {
    // Changing the primary changes summary scalars, so both the details key and
    // every paginated list query for the workspace are invalidated.
    queryClient.invalidateQueries({
        queryKey: counterpartiesQueryKeys.byId(workspaceId, counterpartyId),
    });
    queryClient.invalidateQueries({
        queryKey: counterpartiesQueryKeys.query(workspaceId),
    });
}

export function useCreateCounterpartyContactMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            counterpartyId,
            body,
        }: {
            counterpartyId: string;
            body: CreateCounterpartyContactBody;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            const response =
                await counterpartyContractClient.createCounterpartyContact({
                    params: { workspaceId, counterpartyId },
                    body,
                });
            if (response.status !== 201) {
                throw new Error("Failed to create contact");
            }
            return response.body.contact;
        },
        onSuccess: (_contact, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useUpdateCounterpartyContactMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (variables: {
            counterpartyId: string;
            contactId: string;
            body: UpdateCounterpartyContactBody;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            const response =
                await counterpartyContractClient.updateCounterpartyContact({
                    params: {
                        workspaceId,
                        counterpartyId: variables.counterpartyId,
                        contactId: variables.contactId,
                    },
                    body: variables.body,
                });
            if (response.status !== 200) {
                throw new Error("Failed to update contact");
            }
            return response.body.contact;
        },
        onSuccess: (_contact, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useDeleteCounterpartyContactMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            counterpartyId,
            contactId,
        }: {
            counterpartyId: string;
            contactId: string;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            await counterpartyContractClient.deleteCounterpartyContact({
                params: { workspaceId, counterpartyId, contactId },
            });
        },
        onSuccess: (_result, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useSetPrimaryCounterpartyContactMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            counterpartyId,
            contactId,
        }: {
            counterpartyId: string;
            contactId: string;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            const response =
                await counterpartyContractClient.setPrimaryCounterpartyContact({
                    params: { workspaceId, counterpartyId, contactId },
                });
            if (response.status !== 200) {
                throw new Error("Failed to set primary contact");
            }
            return response.body.contact;
        },
        onSuccess: (_contact, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useCreateCounterpartyAddressMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            counterpartyId,
            body,
        }: {
            counterpartyId: string;
            body: CreateCounterpartyAddressBody;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            const response =
                await counterpartyContractClient.createCounterpartyAddress({
                    params: { workspaceId, counterpartyId },
                    body,
                });
            if (response.status !== 201) {
                throw new Error("Failed to create address");
            }
            return response.body.address;
        },
        onSuccess: (_address, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useUpdateCounterpartyAddressMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (variables: {
            counterpartyId: string;
            addressId: string;
            body: UpdateCounterpartyAddressBody;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            const response =
                await counterpartyContractClient.updateCounterpartyAddress({
                    params: {
                        workspaceId,
                        counterpartyId: variables.counterpartyId,
                        addressId: variables.addressId,
                    },
                    body: variables.body,
                });
            if (response.status !== 200) {
                throw new Error("Failed to update address");
            }
            return response.body.address;
        },
        onSuccess: (_address, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useDeleteCounterpartyAddressMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            counterpartyId,
            addressId,
        }: {
            counterpartyId: string;
            addressId: string;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            await counterpartyContractClient.deleteCounterpartyAddress({
                params: { workspaceId, counterpartyId, addressId },
            });
        },
        onSuccess: (_result, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useSetPrimaryCounterpartyAddressMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            counterpartyId,
            addressId,
        }: {
            counterpartyId: string;
            addressId: string;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            const response =
                await counterpartyContractClient.setPrimaryCounterpartyAddress({
                    params: { workspaceId, counterpartyId, addressId },
                });
            if (response.status !== 200) {
                throw new Error("Failed to set primary address");
            }
            return response.body.address;
        },
        onSuccess: (_address, { counterpartyId }) => {
            if (workspaceId) {
                invalidateCounterpartyDetails(
                    queryClient,
                    workspaceId,
                    counterpartyId,
                );
            }
        },
    });
}

export function useCopyGlobalCounterpartyToLocalMutation(
    onSuccess?: (localCounterparty: Counterparty) => void,
) {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            data: Omit<
                ServerInferRequest<
                    typeof counterpartyContract.copyGlobalCounterparty
                >["body"],
                "workspaceId"
            >,
        ) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const response =
                await counterpartyContractClient.copyGlobalCounterparty({
                    body: {
                        ...data,
                        workspaceId,
                    },
                });

            return response.body.localCounterparty;
        },
        onSuccess: (localCounterparty, data) => {
            handleCounterpartyUpdate(
                queryClient,
                data.globalCounterpartyId,
                localCounterparty,
            );
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey: counterpartiesQueryKeys.byId(
                        workspaceId,
                        data.globalCounterpartyId,
                    ),
                });
                queryClient.invalidateQueries({
                    queryKey: counterpartiesQueryKeys.byId(
                        workspaceId,
                        localCounterparty.id,
                    ),
                });
            }
            onSuccess?.(localCounterparty);
        },
    });
}

export interface CounterpartyRemovalOptions {
    force?: boolean;
    disableInsteadOfDelete?: boolean;
}

export function isCounterpartyLockedTransactionsError(error: unknown): boolean {
    return Boolean(
        isAxiosError(error) && error.response?.data?.lockedTransactionsFound,
    );
}

export function isCounterpartyLinkedDataError(error: unknown): boolean {
    return Boolean(
        isAxiosError(error) && error.response?.data?.linkedDataFound,
    );
}

export function isCounterpartyBillOrInvoiceLinksError(error: unknown): boolean {
    return Boolean(
        isAxiosError(error) && error.response?.data?.billOrInvoiceLinksFound,
    );
}

// The counterparty may have been disabled rather than removed, so refetch the
// list instead of optimistically dropping the row.
function refetchCounterpartyListAfterDisable(
    queryClient: QueryClient,
    workspaceId: string,
) {
    invalidateCounterpartyQueriesForWorkspace(workspaceId);
    queryClient.invalidateQueries({
        queryKey: counterpartiesQueryKeys.byIdAll(workspaceId),
    });
}

export function useCounterpartyRemovalMutation(counterparty: Counterparty) {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (options: CounterpartyRemovalOptions | void) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            return await counterpartyContractClient.deleteCounterparty({
                params: {
                    workspaceId,
                    counterpartyId: counterparty.id,
                },
                query: {
                    force: options?.force ? "true" : undefined,
                    disableInsteadOfDelete: options?.disableInsteadOfDelete
                        ? "true"
                        : undefined,
                },
            });
        },
        onSuccess: (_, options) => {
            if (options?.disableInsteadOfDelete) {
                refetchCounterpartyListAfterDisable(
                    queryClient,
                    workspaceId ?? "",
                );
                return;
            }
            handleCounterpartyRemoval(
                queryClient,
                counterparty.id,
                workspaceId ?? "",
            );
        },
    });
}

export function useCounterpartyMergingMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            data: ServerInferRequest<
                typeof counterpartyContract.mergeCounterparties
            >["body"],
        ) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            return await counterpartyContractClient.mergeCounterparties({
                params: {
                    workspaceId,
                },
                body: data,
            });
        },
        onSuccess: (_, body) => {
            if (body.disableCounterpartyToMerge) {
                refetchCounterpartyListAfterDisable(
                    queryClient,
                    workspaceId ?? "",
                );
                return;
            }
            handleCounterpartyRemoval(
                queryClient,
                body.counterpartyToMergeId,
                workspaceId ?? "",
            );
        },
    });
}

type CounterpartyRows = GetCounterpartiesResponse["data"];

type CounterpartyListCache =
    | GetCounterpartiesResponse
    | InfiniteData<GetCounterpartiesResponse, number>;

// Caches under query(workspaceId) hold one response or the picker's pages.
// An empty cache is left alone: its shape is unknown, so writing a single
// response into a picker cache would drop its pages.
function mapCachedCounterpartyRows(
    queryData: CounterpartyListCache | undefined,
    mapRows: (rows: CounterpartyRows) => CounterpartyRows,
): CounterpartyListCache | undefined {
    if (!queryData) {
        return queryData;
    }
    if ("pages" in queryData) {
        return {
            ...queryData,
            pages: queryData.pages.map((page) => ({
                ...page,
                data: mapRows(page.data),
            })),
        };
    }
    return { ...queryData, data: mapRows(queryData.data ?? []) };
}

function handleCounterpartyUpdate(
    queryClient: QueryClient,
    counterpartyId: string,
    updatedCounterparty: Counterparty,
) {
    const queryKey = counterpartiesQueryKeys.query(
        updatedCounterparty.workspaceId ?? "",
    );
    queryClient.setQueriesData<CounterpartyListCache>(
        { queryKey },
        (queryData) =>
            mapCachedCounterpartyRows(queryData, (rows) =>
                rows.map((row) => ({
                    ...row,
                    counterparty:
                        row.counterparty?.id === counterpartyId
                            ? updatedCounterparty
                            : row.counterparty,
                })),
            ),
    );
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({
        queryKey: counterpartiesQueryKeys.byIdAll(
            updatedCounterparty.workspaceId ?? "",
        ),
    });
}

function handleCounterpartyRemoval(
    queryClient: QueryClient,
    removedCounterpartyId: string,
    workspaceId: string,
) {
    const queryKey = counterpartiesQueryKeys.query(workspaceId);
    queryClient.setQueriesData<CounterpartyListCache>(
        { queryKey },
        (queryData) =>
            mapCachedCounterpartyRows(queryData, (rows) =>
                rows.filter(
                    (row) => row.counterparty?.id !== removedCounterpartyId,
                ),
            ),
    );
    queryClient.invalidateQueries({ queryKey });
    // by-id is a sibling key (not caught by the prefix above); refresh it.
    queryClient.invalidateQueries({
        queryKey: counterpartiesQueryKeys.byIdAll(workspaceId),
    });
}

// Invalidates all cached counterparty list/directory queries for a workspace.
// Used when a transaction-level change (e.g. 1099 exclusion) can affect
// counterparty usage stats/eligibility without the counterparty entity
// itself being mutated, so callers outside this module can't rely on
// handleCounterpartyUpdate to refresh the cache.
export function invalidateCounterpartyQueriesForWorkspace(
    workspaceId: string,
): Promise<void> {
    return globalQueryClient.invalidateQueries({
        queryKey: counterpartiesQueryKeys.query(workspaceId),
    });
}

export async function getCounterparties(
    workspaceId: string,
    query: GetCounterpartiesQuery,
): Promise<GetCounterpartiesResponse> {
    const result = await counterpartyContractClient.getCounterparties({
        params: { workspaceId },
        query,
    });
    return result.body;
}

export async function downloadCounterparties(
    workspaceId: string,
    query: GetCounterpartiesQuery,
) {
    return await counterpartyContractBlobClient.downloadCounterparties({
        params: { workspaceId },
        query,
    });
}

export function useCounterpartyBulkUpdate1099Mutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            counterpartyIds,
            isEligible1099,
        }: {
            counterpartyIds: string[];
            isEligible1099: boolean | null;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await Promise.all(
                counterpartyIds.map((counterpartyId) =>
                    counterpartyContractClient.updateCounterparty({
                        body: { isEligible1099 },
                        params: { workspaceId, counterpartyId },
                    }),
                ),
            );
        },
        // `Promise.all` rejects on the first failed request while others may
        // still have succeeded, so invalidate on settle (success or error) to
        // keep the cached list in sync with the backend.
        onSettled: () => {
            if (!workspaceId) {
                return;
            }
            queryClient.invalidateQueries({
                queryKey: counterpartiesQueryKeys.query(workspaceId),
            });
        },
    });
}

/**
 * Bulk-marks ELIGIBLE_1099 reviewed for the given counterparties — the review
 * half of the Form1099 review task's bulk actions, kept as its own explicit
 * operation (rather than a flag on the eligibility mutation) so callers own
 * its distinct failure semantics, exactly like CounterpartyEligible1099Cell's
 * single-row toggle surfaces a review-marking failure separately from the
 * eligibility change. Rejects if any per-counterparty review request fails so
 * the caller can surface it, and does not touch eligibility itself.
 */
export function useCounterpartyBulkMarkEligible1099ReviewedMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (counterpartyIds: string[]) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await Promise.all(
                counterpartyIds.map((counterpartyId) =>
                    addCounterpartyFieldReviews(workspaceId, counterpartyId, [
                        CounterpartyReviewField.ELIGIBLE_1099,
                    ]),
                ),
            );
        },
        // `Promise.all` rejects on the first failed request while others may
        // still have succeeded, so invalidate on settle (success or error) to
        // keep the cached list in sync with the backend.
        onSettled: () => {
            if (!workspaceId) {
                return;
            }
            queryClient.invalidateQueries({
                queryKey: counterpartiesQueryKeys.query(workspaceId),
            });
        },
    });
}

export function useCounterpartyBulkDeleteMutation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (counterpartyIds: string[]) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await Promise.all(
                counterpartyIds.map((counterpartyId) =>
                    counterpartyContractClient.deleteCounterparty({
                        params: { workspaceId, counterpartyId },
                        query: { force: "true" },
                    }),
                ),
            );
        },
        // `Promise.all` rejects on the first failed request while others may
        // still have succeeded, so invalidate on settle (success or error) to
        // keep the cached list in sync with the backend.
        onSettled: () => {
            if (!workspaceId) {
                return;
            }
            queryClient.invalidateQueries({
                queryKey: counterpartiesQueryKeys.query(workspaceId),
            });
        },
    });
}

export function useCounterpartiesQuery(
    query: GetCounterpartiesQuery,
    options?: { enabled?: boolean },
) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: counterpartiesQueryKeys.queryWithParams(
            workspaceId ?? "",
            query,
        ),
        queryFn: () => {
            if (!workspaceId) {
                return { data: [] };
            }

            return getCounterparties(workspaceId, query);
        },
        enabled: options?.enabled,
    });
}

export const COUNTERPARTY_PICKER_PAGE_SIZE = 50;

export type CounterpartyPickerQuery = Omit<
    GetCounterpartiesQuery,
    "limit" | "offset"
>;

// Page order can shift between requests (name ties, new counterparties), so a
// row may come back on two pages; keep its first appearance.
function flattenCounterpartyPages(
    data: InfiniteData<GetCounterpartiesResponse, number>,
): Counterparty[] {
    const counterpartiesById = new Map<string, Counterparty>();
    for (const page of data.pages) {
        for (const { counterparty } of page.data) {
            if (!counterpartiesById.has(counterparty.id)) {
                counterpartiesById.set(counterparty.id, counterparty);
            }
        }
    }
    return [...counterpartiesById.values()];
}

/**
 * Counterparties for a picker, loaded 50 at a time with `offset`. The endpoint
 * returns no total, so a full page means there may be more. `data` is the
 * loaded pages flattened; pass the result to `toPickerLoadMore` for the
 * Picker's paging props.
 */
export function useCounterpartyPickerQuery(
    query: CounterpartyPickerQuery,
    options: { enabled?: boolean; staleTime?: number } = {},
) {
    const { workspaceId } = useScope();

    return useInfiniteQuery({
        queryKey: counterpartiesQueryKeys.picker(workspaceId ?? "", query),
        queryFn: ({ pageParam }) => {
            if (!workspaceId) {
                return { data: [] };
            }
            return getCounterparties(workspaceId, {
                ...query,
                limit: COUNTERPARTY_PICKER_PAGE_SIZE,
                offset: pageParam,
            });
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, _pages, lastOffset) =>
            lastPage.data.length === COUNTERPARTY_PICKER_PAGE_SIZE
                ? lastOffset + COUNTERPARTY_PICKER_PAGE_SIZE
                : undefined,
        select: flattenCounterpartyPages,
        enabled: Boolean(workspaceId) && (options.enabled ?? true),
        staleTime: options.staleTime,
    });
}

export async function addCounterpartyFieldReviews(
    workspaceId: string,
    counterpartyId: string,
    fields: GenericCounterpartyReviewField[],
): Promise<CounterpartyFieldReviewDto[]> {
    const res = await counterpartyContractClient.addFieldReviews({
        params: { workspaceId, counterpartyId },
        body: { fields },
    });
    if (res.status !== 200) {
        throw new Error("Failed to add field reviews");
    }
    return res.body.reviews;
}

export async function revokeCounterpartyFieldReview(
    workspaceId: string,
    counterpartyId: string,
    field: GenericCounterpartyReviewField,
): Promise<boolean> {
    const res = await counterpartyContractClient.revokeFieldReview({
        params: { workspaceId, counterpartyId, field },
    });
    if (res.status !== 200) {
        throw new Error("Failed to revoke field review");
    }
    return res.body.deleted;
}

// Updates the cached fieldReviews for a single counterparty row across every
// counterparties list query for the workspace, mirroring how
// handleCounterpartyUpdate patches the counterparty entity in place.
export function updateCounterpartyFieldReviewsInCache(
    workspaceId: string,
    counterpartyId: string,
    updater: (
        fieldReviews: CounterpartyFieldReviewDto[],
    ) => CounterpartyFieldReviewDto[],
): void {
    const queryKey = counterpartiesQueryKeys.query(workspaceId);
    globalQueryClient.setQueriesData<CounterpartyListCache>(
        { queryKey },
        (queryData) =>
            mapCachedCounterpartyRows(queryData, (rows) =>
                rows.map((row) =>
                    row.counterparty?.id === counterpartyId
                        ? {
                              ...row,
                              fieldReviews: updater(row.fieldReviews ?? []),
                          }
                        : row,
                ),
            ),
    );
}
